import React from 'react';
import { useFormikContext } from 'formik';

import { EntityModules } from 'core/constants';

import EntitySearchField from 'components/EntitySearchField';
import GroupLicenceNotice from 'components/GroupLicenceNotice';
import UserAndGroupPicker from 'components/SharedFormComponents/UserAndGroupPicker';
import { StyledTextField } from 'components/Input/styles';

import { UuidableName } from 'utils/api/types';

import { useStore } from 'utils/hooks/store';

import { ScheduleFormValues } from 'utils/api/modules/schedules/schedules.types';

import { SectionContainer, SectionHeader, FieldRow, FieldLabel, RequiredIndicator } from '../../styles';
import TemplateSearchField from '../TemplateSearchField';

const GeneralDetailsSection: React.FunctionComponent<{ usesSubmissionForm: boolean }> = ({ usesSubmissionForm }) => {
  const { session } = useStore();

  const activeEntityModules = session.user?.activeEntityModules;

  const showSites = activeEntityModules?.includes(EntityModules.Sites);
  const showAssets = activeEntityModules?.includes(EntityModules.Assets);
  const showContacts = activeEntityModules?.includes(EntityModules.Contacts);

  const { values, initialValues, setFieldValue, errors, touched } = useFormikContext<ScheduleFormValues>();

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('name', event.target.value);
  };

  const handleTemplateChange = (template: UuidableName | null): void => {
    setFieldValue('template', template);
    if (template && !values.name) {
      setFieldValue('name', template.name);
    }
  };

  const handleEntityChange = (field: string, value: UuidableName | UuidableName[] | null): void => {
    setFieldValue(field, value);
  };

  return (
    <SectionContainer>
      <SectionHeader>General Details</SectionHeader>

      <FieldRow>
        <FieldLabel>
          Name
          <RequiredIndicator>*</RequiredIndicator>
        </FieldLabel>
        <StyledTextField
          fullWidth
          value={values.name}
          onChange={handleNameChange}
          placeholder="Enter schedule name"
          error={touched.name && !!errors.name}
          helperText={touched.name && errors.name ? errors.name : undefined}
        />
      </FieldRow>

      <FieldRow>
        <FieldLabel>
          Audit Template
          <RequiredIndicator>*</RequiredIndicator>
        </FieldLabel>
        <TemplateSearchField
          value={values.template}
          onChange={handleTemplateChange}
          usesSubmissionForm={usesSubmissionForm}
          error={touched.template && !!errors.template}
          helperText={touched.template && errors.template ? errors.template : undefined}
        />
      </FieldRow>

      {showSites && (
        <FieldRow>
          <FieldLabel>Site</FieldLabel>
          <EntitySearchField
            field="site"
            module={EntityModules.Sites}
            data={values.site}
            updateField={(_, value) => handleEntityChange('site', value)}
          />
        </FieldRow>
      )}

      {showAssets && (
        <FieldRow>
          <FieldLabel>Asset</FieldLabel>
          <EntitySearchField
            field="asset"
            module={EntityModules.Assets}
            data={values.asset}
            updateField={(_, value) => handleEntityChange('asset', value)}
          />
        </FieldRow>
      )}

      {showContacts && (
        <FieldRow>
          <FieldLabel>Contact</FieldLabel>
          <EntitySearchField
            field="contact"
            module={EntityModules.Contacts}
            data={values.contact}
            updateField={(_, value) => handleEntityChange('contact', value)}
          />
        </FieldRow>
      )}

      <FieldRow>
        <FieldLabel>
          Assigned To
          <RequiredIndicator>*</RequiredIndicator>
        </FieldLabel>
        <UserAndGroupPicker
          id="schedule-assignee-picker"
          data-testid="schedule-assignee-picker"
          value={values.assignees}
          onChange={(next) => setFieldValue('assignees', next)}
          enableGroups={!!session.user?.groupsEnabled}
          placeholder="Select user"
          groupsPlaceholder="Choose option(s)"
          licenses="workflows"
          error={touched.assignees && !!errors.assignees}
          helperText={touched.assignees && errors.assignees ? String(errors.assignees) : undefined}
        />
        <GroupLicenceNotice
          value={values.assignees}
          savedValue={initialValues.assignees}
          data-testid="schedule-group-licence-notice"
        />
      </FieldRow>
    </SectionContainer>
  );
};

export default GeneralDetailsSection;
